import { z } from 'zod';
import { ToolNameSchema } from './access.js';
import { IdSchema, LocaleSchema, TenantIdSchema } from './common.js';

/*
 * Esquema do lastrouter.yaml: só infraestrutura, quem muda é a TI.
 * Tudo tem padrão; o arquivo mínimo diz onde está a fonte, qual é o IdP e o endereço público.
 * Nenhum endereço de cliente ou da Cluster tem padrão aqui: vem sempre do arquivo.
 */

/**
 * Referência a segredo: env:NOME, file:caminho, certstore:LocalMachine/My/<thumbprint>
 * ou kms:v1:... (cifrado com a chave do tenant, na Cloud).
 */
export const SecretRefSchema = z
  .string()
  .regex(/^(env|file|certstore|kms):.+$|^keyvault:[a-z0-9-]{3,24}\/[A-Za-z0-9-]{1,127}$/, 'use env:NOME, file:caminho, certstore:LocalMachine/My/<thumbprint>, keyvault:<cofre>/<segredo> ou kms:');

/** keyvault:<cofre>/<segredo>: segredo do Azure Key Vault, lido na subida com a identidade gerenciada. */
export const VAULT_REF_RE = /^keyvault:([a-z0-9-]{3,24})\/([A-Za-z0-9-]{1,127})$/;

/** Resiliência em volta de qualquer fonte: retentativa curta e circuit breaker por conexão. */
export const ResilienceSchema = z.object({
  retries: z.number().int().min(0).max(5).default(1),
  backoff_ms: z.number().int().min(10).max(10_000).default(200),
  breaker_failures: z.number().int().min(1).max(100).default(5),
  breaker_reset_seconds: z.number().int().min(1).max(3600).default(30),
  /** Cache curto de listas de valores, por tenant e usuário (a section access muda por pessoa). */
  values_cache_seconds: z.number().int().min(0).max(3600).default(60),
});
const RESILIENCE_DEFAULTS = { retries: 1, backoff_ms: 200, breaker_failures: 5, breaker_reset_seconds: 30, values_cache_seconds: 60 };

/** Usuário nas fontes em nuvem: o login vira e-mail ou subject do IdP da fonte. */
export const CloudUserRuleSchema = z.object({
  id_from: z.union([z.enum(['upn']), z.string().regex(/^claim:[A-Za-z0-9_.-]+$/)]).default('upn'),
  case: z.enum(['preserve', 'upper', 'lower']).default('lower'),
  domains: z.array(z.string().min(1).transform((d) => d.toLowerCase())).optional(),
});
export type CloudUserRule = z.infer<typeof CloudUserRuleSchema>;

const HostnameSchema = z
  .string()
  .min(1)
  .transform((h) => h.replace(/^https?:\/\//, '').replace(/\/+$/, ''))
  .refine((h) => /^[A-Za-z0-9.-]+$/.test(h), 'informe só o hostname, sem caminho (ex.: qlik.cliente.intra)');

const PrefixSchema = z
  .string()
  .transform((p) => p.replace(/^\/+|\/+$/g, ''))
  .refine((p) => /^[A-Za-z0-9_-]*$/.test(p), 'prefixo do virtual proxy só aceita letras, números, _ e -');

export const UserRuleSchema = z.object({
  /** Diretório do usuário na fonte (no Qlik, o user directory do QMC). */
  directory: z.string().min(1),
  /** De onde sai o login: parte local do UPN, UPN inteiro ou um claim do token. */
  id_from: z.union([z.enum(['upn_local_part', 'upn']), z.string().regex(/^claim:[A-Za-z0-9_.-]+$/)]).default('upn_local_part'),
  case: z.enum(['preserve', 'upper', 'lower']).default('preserve'),
  /**
   * Domínios de UPN aceitos (ex.: [sefaz.rs.gov.br]). Com upn_local_part, sem esta lista,
   * x@a.gov.br e x@b.gov.br viram o mesmo usuário na fonte; o doctor avisa.
   */
  domains: z.array(z.string().min(1).transform((d) => d.toLowerCase())).optional(),
});
export type UserRule = z.infer<typeof UserRuleSchema>;

export const QlikOnPremSourceSchema = z.object({
  id: IdSchema,
  type: z.literal('qlik-onprem'),
  host: HostnameSchema,
  port: z.number().int().min(1).max(65535).default(443),
  virtual_proxy: PrefixSchema,
  signing_key: SecretRefSchema,
  /** CA extra para confiar no certificado do Qlik. Nunca há opção de desligar a verificação de TLS. */
  ca_file: z.string().min(1).optional(),
  jwt_attributes: z
    .object({ user_id: z.string().min(1).default('userId'), user_directory: z.string().min(1).default('userDirectory') })
    .default({ user_id: 'userId', user_directory: 'userDirectory' }),
  jwt_ttl_seconds: z.number().int().min(30).max(3600).default(300),
  user: UserRuleSchema,
  sessions: z
    .object({
      max_per_instance: z.number().int().min(1).max(1000).default(60),
      idle_ttl_seconds: z.number().int().min(10).max(3600).default(300),
      acquire_timeout_ms: z.number().int().min(100).max(120_000).default(10_000),
      open_timeout_ms: z.number().int().min(1000).max(120_000).default(20_000),
    })
    .default({ max_per_instance: 60, idle_ttl_seconds: 300, acquire_timeout_ms: 10_000, open_timeout_ms: 20_000 }),
  resilience: ResilienceSchema.default(RESILIENCE_DEFAULTS),
  /** Usuário usado pelo doctor para abrir uma sessão de teste (ex.: CLUSTER\maria). */
  test_user: z.string().min(1).optional(),
  /**
   * Usuários que `lastrouter eval` pode impor nesta fonte. A suíte de perguntas é escrita pelos
   * donos do assunto; quem decide como quem a avaliação roda é a TI, aqui.
   */
  test_users: z.array(z.string().min(1)).default([]),
});
export type QlikOnPremSourceConfig = z.infer<typeof QlikOnPremSourceSchema>;

/**
 * Qlik Cloud (Cloud v1). Três modos de entrar como o usuário:
 * - oauth-impersonation: cliente OAuth com impersonação, o Lastrouter pede token em nome de cada usuário;
 * - oauth-delegated: cada usuário autoriza uma vez e o refresh token fica cifrado;
 * - jwt: IdP do tipo JWT cadastrado no tenant Qlik com a chave pública do Lastrouter.
 */
export const QlikCloudSourceSchema = z.object({
  id: IdSchema,
  type: z.literal('qlik-cloud'),
  /** https://<tenant>.<região>.qlikcloud.com */
  tenant_url: z
    .string()
    .url()
    .refine((u) => u.startsWith('https://'), 'use https:// (o segredo do cliente OAuth vai nessa conexão)')
    .transform((u) => u.replace(/\/+$/, '')),
  auth: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('oauth-impersonation'), client_id: z.string().min(1), client_secret: SecretRefSchema }),
    z.object({ mode: z.literal('oauth-delegated'), client_id: z.string().min(1), client_secret: SecretRefSchema.optional() }),
    z.object({ mode: z.literal('jwt'), issuer: z.string().min(1), key_id: z.string().min(1), signing_key: SecretRefSchema }),
  ]),
  user: CloudUserRuleSchema.default({ id_from: 'upn', case: 'lower' }),
  sessions: z
    .object({
      max_per_instance: z.number().int().min(1).max(1000).default(60),
      idle_ttl_seconds: z.number().int().min(10).max(3600).default(300),
      acquire_timeout_ms: z.number().int().min(100).max(120_000).default(10_000),
      open_timeout_ms: z.number().int().min(1000).max(120_000).default(20_000),
    })
    .default({ max_per_instance: 60, idle_ttl_seconds: 300, acquire_timeout_ms: 10_000, open_timeout_ms: 20_000 }),
  resilience: ResilienceSchema.default(RESILIENCE_DEFAULTS),
  test_user: z.string().min(1).optional(),
  test_users: z.array(z.string().min(1)).default([]),
});
export type QlikCloudSourceConfig = z.infer<typeof QlikCloudSourceSchema>;

/**
 * Power BI (v2). Sempre delegado: troca On-Behalf-Of do token do usuário, e a RLS do modelo vale.
 * Service principal não funciona com RLS. A credencial do app é certificado, não segredo.
 */
export const PowerBiSourceSchema = z.object({
  id: IdSchema,
  type: z.literal('powerbi'),
  /** Tenant do Entra onde está o Power BI. */
  tenant_id: z.string().uuid(),
  client_id: z.string().min(1),
  /** Certificado com chave privada: file:<pem> ou certstore:. */
  certificate: SecretRefSchema,
  authority_host: z.string().url().default('https://login.microsoftonline.com'),
  api_base: z.string().url().refine((u) => u.startsWith('https://'), 'use https://').default('https://api.powerbi.com/v1.0/myorg'),
  resilience: ResilienceSchema.default(RESILIENCE_DEFAULTS),
  test_users: z.array(z.string().min(1)).default([]),
});
export type PowerBiSourceConfig = z.infer<typeof PowerBiSourceSchema>;

export const SourceConfigSchema = z.discriminatedUnion('type', [QlikOnPremSourceSchema, QlikCloudSourceSchema, PowerBiSourceSchema]);
export type SourceConfig = z.infer<typeof SourceConfigSchema>;

export const EntraIdentitySchema = z.object({
  provider: z.literal('entra'),
  tenant_id: z.string().uuid('tenant_id do Entra é um GUID'),
  /** api://lastrouter e/ou o client id do registro do app (tokens v2 trazem o client id em aud). */
  audience: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
  authority_host: z.string().url().default('https://login.microsoftonline.com'),
  /** Escopo delegado exigido no token (claim scp), ex.: access_as_user. */
  required_scope: z.string().min(1).optional(),
  /**
   * Claim de onde sai o login. Tokens v2 só trazem upn se o claim opcional estiver configurado
   * no registro do app (o setup configura). Não há queda para preferred_username nem email.
   */
  user_claim: z.string().min(1).default('upn'),
  /** Convidados (B2B) de outros tenants são recusados, a não ser que isto esteja ligado. */
  allow_guests: z.boolean().default(false),
});

export const JwtIdentitySchema = z
  .object({
    provider: z.literal('jwt'),
    issuer: z.string().min(1),
    audience: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
    jwks_uri: z.string().url().optional(),
    public_key_file: z.string().min(1).optional(),
    algorithms: z.array(z.enum(['RS256', 'RS384', 'RS512', 'ES256', 'ES384', 'PS256', 'EdDSA'])).default(['RS256']),
    user_claim: z.string().min(1).default('upn'),
    subject_claim: z.string().min(1).default('sub'),
    roles_claim: z.string().min(1).default('roles'),
    client_claim: z.string().min(1).default('azp'),
    name_claim: z.string().min(1).default('name'),
  })
  .refine((j) => Boolean(j.jwks_uri) !== Boolean(j.public_key_file), {
    message: 'informe jwks_uri ou public_key_file (um dos dois)',
    path: ['jwks_uri'],
  });

export type EntraIdentityConfig = z.infer<typeof EntraIdentitySchema>;
export type JwtIdentityConfig = z.infer<typeof JwtIdentitySchema>;

export const IdentityConfigSchema = z.discriminatedUnion('provider', [EntraIdentitySchema, JwtIdentitySchema]);
export type IdentityConfig = z.infer<typeof IdentityConfigSchema>;

export const InterpretationConfigSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal('openai'),
    model: z.string().min(1),
    api_key: SecretRefSchema,
    base_url: z.string().url().default('https://api.openai.com/v1'),
    timeout_ms: z.number().int().min(1000).max(120_000).default(15_000),
    reasoning_effort: z.enum(['none', 'minimal', 'low', 'medium']).optional(),
    max_candidates: z.number().int().min(1).max(20).default(5),
    max_values_in_prompt: z.number().int().min(0).max(500).default(50),
    price_per_million: z.object({ input: z.number().min(0), output: z.number().min(0) }).default({ input: 0.1, output: 0.5 }),
  }),
  z.object({
    provider: z.literal('azure-openai'),
    /** https://<recurso>.openai.azure.com */
    endpoint: z.string().url(),
    deployment: z.string().min(1),
    model: z.string().min(1),
    api_key: SecretRefSchema,
    api_version: z.string().min(1).default('2025-04-01-preview'),
    timeout_ms: z.number().int().min(1000).max(120_000).default(15_000),
    reasoning_effort: z.enum(['none', 'minimal', 'low', 'medium']).optional(),
    max_candidates: z.number().int().min(1).max(20).default(5),
    max_values_in_prompt: z.number().int().min(0).max(500).default(50),
    price_per_million: z.object({ input: z.number().min(0), output: z.number().min(0) }).default({ input: 0.1, output: 0.5 }),
  }),
  /** Interpretação por regras, sem LLM. Serve para testes e linha de base. */
  z.object({
    provider: z.literal('rules'),
    max_candidates: z.number().int().min(1).max(20).default(5),
    max_values_in_prompt: z.number().int().min(0).max(100_000).default(10_000),
  }),
]);
export type InterpretationConfig = z.infer<typeof InterpretationConfigSchema>;
export type LlmInterpretationConfig = Extract<InterpretationConfig, { provider: 'openai' | 'azure-openai' }>;

export const ChannelConfigSchema = z.object({
  id: IdSchema.refine((id) => !['default', 'cli', 'desconhecido'].includes(id), 'default, cli e desconhecido são ids reservados'),
  /**
   * Client ids (azp/appid) dos apps que representam este canal. Vazio: o canal só é usado por quem o fixa pela rota
   * (o chat web em chat.channel, ADR 0046), nunca pelo token de um app cliente.
   */
  client_app_ids: z.array(z.string().min(1)).default([]),
  tools: z.array(ToolNameSchema).min(1),
  calls_per_minute: z.number().int().min(1).optional(),
  /**
   * Modo privacidade (ADR 0047): o canal usa o motor do Lastrouter (bot do Teams, chat próprio), que tira os valores
   * privados da LLM e os mostra só para a pessoa. Os demais (Claude e outros clientes MCP) recebem o resultado sem valores.
   */
  shows_private_values: z.boolean().default(false),
  /**
   * Canal de homologação: serve contexto ainda não certificado para produção (ADR 0044), para o cliente testar.
   * Os outros canais só servem contexto liberado quando context_pipeline.require_certification está ligado.
   */
  staging: z.boolean().default(false),
});
export type ChannelConfig = z.infer<typeof ChannelConfigSchema>;

/** Só os provedores com LLM da interpretação: o pipeline de contexto usa o mesmo formato de configuração. */
const PipelineLlmSchema = z.discriminatedUnion('provider', InterpretationConfigSchema.options.filter((o) => o.shape.provider.value !== 'rules') as [
  (typeof InterpretationConfigSchema.options)[0],
  (typeof InterpretationConfigSchema.options)[1],
]);

/** Pipeline de contexto (ADR 0044): a LLM do passo 4 e a exigência de certificação para servir em produção. */
export const ContextPipelineConfigSchema = z.object({
  /** Sem ela, o passo 4 usa a de interpretation quando for LLM; sem nenhuma, o pipeline roda sem interpretação. */
  llm: PipelineLlmSchema.optional(),
  /** Ligado: canal que não é de homologação só serve contexto com certificação "liberado para produção" na versão em uso. */
  require_certification: z.boolean().default(false),
  /** Prazo de uma execução inteira (extração, engine, LLM e saída). Até 60: aos 70 (CONTEXT_RUN_MAX_MS) a execução é dada como morta e outra pode começar. */
  timeout_minutes: z.number().int().min(1).max(60).default(30),
});
export type ContextPipelineConfig = z.infer<typeof ContextPipelineConfigSchema>;

/**
 * Chat próprio no navegador (/chat, ADR 0046). Sem esta seção, o chat fica desligado. O canal é fixo pela rota, não pelo
 * app do token: precisa existir em channels com as ferramentas liberadas.
 */
export const ChatConfigSchema = z.object({
  channel: IdSchema.default('web'),
  /** Client id do registro do app no Entra que o navegador usa no login (SPA). Obrigatório com identity.provider entra. */
  client_id: z.string().min(1).optional(),
  /** Escopo pedido no login. Padrão: a primeira audiência api:// (ou api://<client_id>) com identity.required_scope (ou access_as_user). */
  scope: z.string().min(1).optional(),
  /** LLM do chat. Sem ela, vale context_pipeline.llm e depois a da interpretação. */
  llm: PipelineLlmSchema.optional(),
  /** Teto de perguntas à LLM por pessoa e por dia no chat web. */
  daily_turns: z.number().int().min(1).default(200),
});
export type ChatConfig = z.infer<typeof ChatConfigSchema>;

const TlsSchema = z.union([
  z.object({ cert_file: z.string().min(1), key_file: z.string().min(1) }),
  z.object({ pfx_file: z.string().min(1), passphrase: SecretRefSchema.optional() }),
]);

/** Quantas ocorrências na janela, pelo mesmo IP, disparam um alerta. */
export const AlertThresholdSchema = z.object({
  count: z.number().int().min(1).max(100_000),
  window_seconds: z.number().int().min(10).max(86_400),
});

export const LimitsSchema = z.object({
  calls_per_minute: z.number().int().min(1).default(30),
  max_rows: z.number().int().min(1).max(100_000).default(1000),
  query_timeout_ms: z.number().int().min(1000).max(300_000).default(25_000),
  max_concurrent_requests: z.number().int().min(1).max(10_000).default(16),
  queue_size: z.number().int().min(0).max(100_000).default(128),
  queue_timeout_ms: z.number().int().min(0).max(120_000).default(20_000),
  max_request_bytes: z.number().int().min(1024).max(10_000_000).default(1_000_000),
});
export type LimitsConfig = z.infer<typeof LimitsSchema>;

export const LastrouterConfigSchema = z
  .object({
    edition: z.enum(['selfhosted', 'cloud']).default('selfhosted'),
    /** Endereço público do serviço (sem /mcp). Vai no Protected Resource Metadata. */
    public_url: z
      .string()
      .url()
      .transform((u) => u.replace(/\/+$/, '')),
    tenant_id: TenantIdSchema.default('default'),
    instance_id: z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/).optional(),
    locale: LocaleSchema.default('pt-BR'),
    timezone: z.string().min(1).default('America/Sao_Paulo'),
    server: z
      .object({
        host: z.string().min(1).default('0.0.0.0'),
        port: z.number().int().min(1).max(65535).default(3000),
        /** Confiar em X-Forwarded-* do reverse proxy. Ligue só atrás de um proxy conhecido. */
        trust_proxy: z.union([z.boolean(), z.string()]).default(false),
        tls: TlsSchema.optional(),
        /** Origens de navegador aceitas no /mcp. Vazio (padrão): chamada de navegador é recusada. */
        allowed_origins: z.array(z.string().url()).default([]),
      })
      .default({ host: '0.0.0.0', port: 3000, trust_proxy: false, allowed_origins: [] }),
    identity: IdentityConfigSchema,
    /** Na Cloud, as fontes de cada cliente ficam no registro de tenants; aqui pode ficar vazio. */
    sources: z.array(SourceConfigSchema).default([]),
    interpretation: InterpretationConfigSchema.optional(),
    context_pipeline: ContextPipelineConfigSchema.default({ require_certification: false, timeout_minutes: 30 }),
    chat: ChatConfigSchema.optional(),
    catalog: z.object({ path: z.string().min(1).default('./contexto') }).default({ path: './contexto' }),
    state: z
      .object({
        path: z.string().min(1).default('./state'),
        /** files na POC; postgres a partir do Self-hosted v1 e sempre na Cloud. */
        backend: z.enum(['files', 'postgres']).default('files'),
      })
      .default({ path: './state', backend: 'files' }),
    database: z
      .object({
        url: SecretRefSchema,
        schema: z.string().regex(/^[a-z_][a-z0-9_]{0,62}$/).default('lastrouter'),
        max_connections: z.number().int().min(1).max(200).default(10),
        /**
         * Papel sem BYPASSRLS para as transações, quando a URL usa um usuário com mais poder
         * (ex.: o dono das tabelas, para as migrações). Sem ele, a conexão precisa ser de um papel comum.
         */
        app_role: z.string().regex(/^[a-z_][a-z0-9_]{0,62}$/).optional(),
      })
      .optional(),
    /**
     * Papéis do IdP dizem o que a pessoa pode fazer; os apps que ela enxerga vêm da fonte (ADR 0036).
     * Leitor pergunta e analisa; Editor também propõe correção de contexto pelo chat; o Admin (admin.role) inclui os dois.
     */
    access: z
      .object({
        reader_role: z.string().min(1).default('Lastrouter.Leitor'),
        editor_role: z.string().min(1).default('Lastrouter.Editor'),
        // ponytail: lista de apps por pessoa guardada em memória por instância. Mais tempo, menos sessões no
        // Qlik (5 paralelas por usuário) e mais demora para refletir uma permissão nova ou retirada.
        visible_apps_cache_minutes: z.number().min(0).max(60).default(5),
      })
      .default({ reader_role: 'Lastrouter.Leitor', editor_role: 'Lastrouter.Editor', visible_apps_cache_minutes: 5 }),
    /**
     * Itens do modelo do app fora do catálogo (ADR 0037): describe_app mostra os campos e itens mestres que o
     * catálogo não cobre, e query aceita esses itens com resultado não certificado. O catálogo continua primeiro.
     * Na Cloud, o registro do tenant pode desligar com model_fallback: false.
     */
    model_fallback: z
      .object({
        enabled: z.boolean().default(true),
        /** Ids de assunto (app no catálogo) em que só vale o catálogo. */
        disabled_apps: z.array(IdSchema).default([]),
        // ponytail: modelo guardado em memória por (tenant, fonte, app). Ele só muda na recarga do app, e cada
        // leitura abre uma sessão no Qlik (5 paralelas por usuário). Campo novo demora até este prazo para aparecer.
        cache_minutes: z.number().min(0).max(1440).default(30),
      })
      .default({ enabled: true, disabled_apps: [], cache_minutes: 30 }),
    /** Painel administrativo, servido pelo próprio serviço na mesma origem. */
    admin: z
      .object({
        enabled: z.boolean().default(true),
        /** App role que dá acesso de administrador ao painel. */
        role: z.string().min(1).default('Lastrouter.Admin'),
        /** Chave da sessão do painel (cookie cifrado). Mesma em todas as instâncias. */
        session_secret: SecretRefSchema.optional(),
        session_hours: z.number().int().min(1).max(24).default(8),
        /** Login do painel no IdP (código de autorização). Sem isto, só o login de desenvolvimento. */
        login: z.object({ client_id: z.string().min(1), client_secret: SecretRefSchema }).optional(),
        /** Pasta do build do painel. Padrão: ao lado do executável. */
        panel_dir: z.string().min(1).optional(),
      })
      .default({ enabled: true, role: 'Lastrouter.Admin', session_hours: 8 }),
    quality: z
      .object({
        /** Guarda o texto das perguntas (sem resultado) para a avaliação online e a suíte. */
        store_questions: z.boolean().default(true),
        questions_retention_days: z.number().int().min(1).max(3650).default(90),
      })
      .default({ store_questions: true, questions_retention_days: 90 }),
    telemetry: z
      .object({
        otlp_endpoint: z.string().url(),
        service_name: z.string().min(1).default('lastrouter'),
        sample_ratio: z.number().min(0).max(1).default(1),
        headers: z.record(z.string(), SecretRefSchema).default({}),
      })
      .optional(),
    /**
     * Alertas de segurança: rajadas de token inválido (401), acesso negado (403) e limite excedido (429)
     * por IP, ações de administrador no painel e corrente de auditoria quebrada. Sempre no log
     * (alerta_seguranca); com webhook, também enviados para fora (Teams, Slack, Event Grid, SIEM).
     */
    alerts: z
      .object({
        webhook: z
          .object({
            url: z.string().url().refine((u) => u.startsWith('https://'), 'o webhook de alertas precisa ser https://'),
            /** json: o alerta como está. cloudevents: envelope CloudEvents 1.0 (Event Grid e coletores compatíveis). */
            format: z.enum(['json', 'cloudevents']).default('json'),
            /** Cabeçalhos de autenticação do destino, ex.: aeg-sas-key no Event Grid. Valores sempre por referência. */
            headers: z.record(z.string().regex(/^[A-Za-z0-9-]{1,64}$/), SecretRefSchema).default({}),
            /** Gravidade mínima enviada ao webhook; abaixo dela, só o log. Padrão: aviso (login e ações do painel ficam no log). */
            min_severity: z.enum(['info', 'aviso', 'critico']).default('aviso'),
          })
          .optional(),
        auth_failures: AlertThresholdSchema.default({ count: 20, window_seconds: 300 }),
        refusals: AlertThresholdSchema.default({ count: 20, window_seconds: 300 }),
        rate_limited: AlertThresholdSchema.default({ count: 30, window_seconds: 300 }),
        admin_actions: z.boolean().default(true),
      })
      .default({
        auth_failures: { count: 20, window_seconds: 300 },
        refusals: { count: 20, window_seconds: 300 },
        rate_limited: { count: 30, window_seconds: 300 },
        admin_actions: true,
      }),
    /** Exportação da auditoria para o SIEM do cliente (v2). */
    siem: z
      .object({
        kind: z.enum(['syslog', 'http']),
        /** syslog: host:porta (TLS); http: URL do coletor (ex.: Splunk HEC). */
        target: z.string().min(1),
        format: z.enum(['json', 'cef']).default('json'),
        token: SecretRefSchema.optional(),
        ca_file: z.string().min(1).optional(),
      })
      // O token do coletor e a auditoria não trafegam em claro.
      .refine((s) => s.kind !== 'http' || s.target.startsWith('https://'), { message: 'coletor HTTP do SIEM precisa ser https://', path: ['target'] })
      .optional(),
    /** Edição Cloud: domínio dos tenants, chaves por tenant e coordenação entre instâncias. */
    cloud: z
      .object({
        base_domain: z.string().regex(/^[a-z0-9.-]+$/),
        kms: z.discriminatedUnion('provider', [
          z.object({ provider: z.literal('local'), master_key: SecretRefSchema }),
          z.object({ provider: z.literal('azure-keyvault'), vault_url: z.string().url(), key_name: z.string().min(1) }),
        ]),
        redis_url: SecretRefSchema.optional(),
        /** Client id do app multi-tenant da Cluster no Entra (audiência dos tokens). */
        entra_app_id: z.string().min(1),
      })
      .optional(),
    logs: z
      .object({
        path: z.string().min(1).default('./logs'),
        level: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),
        retention_days: z.number().int().min(1).max(3650).default(90),
        stdout: z.boolean().default(true),
      })
      .default({ path: './logs', level: 'info', retention_days: 90, stdout: true }),
    results: z
      .object({ retention_days: z.number().int().min(1).max(3650).default(90) })
      .default({ retention_days: 90 }),
    limits: LimitsSchema.default({
      calls_per_minute: 30,
      max_rows: 1000,
      query_timeout_ms: 25_000,
      max_concurrent_requests: 16,
      queue_size: 128,
      queue_timeout_ms: 20_000,
      max_request_bytes: 1_000_000,
    }),
    channels: z.array(ChannelConfigSchema).default([]),
    /** Com canais cadastrados, um app cliente desconhecido é recusado, a não ser que isto esteja ligado. */
    allow_unknown_clients: z.boolean().default(false),
    /** Ferramentas de um app cliente desconhecido, quando allow_unknown_clients está ligado. Nenhuma por padrão. */
    unknown_client_tools: z.array(ToolNameSchema).default([]),
    license: z
      .object({
        file: z.string().min(1),
        /** Chaves públicas Ed25519 (PEM) para desenvolvimento e testes. O build empacotado ignora: vale só a embutida. */
        public_keys: z.array(z.string().min(1)).default([]),
        /** Check-in no back office da Cluster (v1). Sem isto, a licença só é conferida localmente. */
        checkin: z
          .object({
            url: z.string().url(),
            interval_hours: z.number().int().min(1).max(168).default(24),
            /** Dias de funcionamento sem check-in antes de considerar a licença vencida. */
            grace_days: z.number().int().min(1).max(365).default(30),
          })
          .optional(),
      })
      .optional(),
  })
  .superRefine((c, ctx) => {
    if (c.edition === 'selfhosted' && c.sources.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['sources'], message: 'cadastre ao menos uma fonte em sources' });
    }
    if (c.edition === 'cloud' && !c.cloud) ctx.addIssue({ code: 'custom', path: ['cloud'], message: 'a edição Cloud exige a seção cloud' });
    if ((c.edition === 'cloud' || c.state.backend === 'postgres') && !c.database) {
      ctx.addIssue({ code: 'custom', path: ['database'], message: 'state.backend postgres (e a edição Cloud) exige database.url' });
    }
    const ids = c.sources.map((s) => s.id);
    ids.forEach((id, i) => {
      if (ids.indexOf(id) !== i) ctx.addIssue({ code: 'custom', path: ['sources', i, 'id'], message: `fonte repetida: ${id}` });
    });
    if (c.chat && !c.channels.some((ch) => ch.id === c.chat!.channel)) {
      ctx.addIssue({ code: 'custom', path: ['chat', 'channel'], message: `o chat usa o canal ${c.chat.channel}, que não está em channels` });
    }
    if (c.chat && c.identity.provider === 'entra' && !c.chat.client_id) {
      ctx.addIssue({ code: 'custom', path: ['chat', 'client_id'], message: 'com identity.provider entra, o chat precisa do client_id do app (SPA) no Entra' });
    }
    const clients = new Map<string, string>();
    c.channels.forEach((ch, i) => {
      for (const app of ch.client_app_ids) {
        const other = clients.get(app);
        if (other) {
          ctx.addIssue({ code: 'custom', path: ['channels', i, 'client_app_ids'], message: `app cliente ${app} já está no canal ${other}` });
        }
        clients.set(app, ch.id);
      }
    });
  });
export type LastrouterConfig = z.infer<typeof LastrouterConfigSchema>;
export type LastrouterConfigInput = z.input<typeof LastrouterConfigSchema>;
